import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { SpringValue } from '@react-spring/web';
import { animated, config, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { CardMeta } from '@sorare/core/src/components/card/CardMeta';
import { StrokedCard } from '@sorare/core/src/components/card/StrokedCard';
import { waitMs } from '@sorare/core/src/lib/animation/waitMs';
import { range } from '@sorare/core/src/lib/arrays';
import {
  CARD_ASPECT_RATIO,
  getCardBorderRadiusWithOffsets,
} from '@sorare/core/src/lib/cards';
import { getRarityColor } from '@sorare/core/src/lib/colors';

import { AnimatedCardFooter } from '#src/components/essence/CardPullExperience/CardsReveal/AnimatedCardFooter/index.tsx';
import { PickedCardRevealer } from '#src/components/essence/CardPullExperience/CardsReveal/PickedCardRevealer/index.tsx';
import { RemainingCardRevealer } from '#src/components/essence/CardPullExperience/CardsReveal/RemainingCardRevealer/index.tsx';
import { RevealingCardPlaceholder } from '#src/components/essence/CardPullExperience/CardsReveal/RevealingCardPlaceholder/index.tsx';
import type { CardRevealUIState } from '#src/components/essence/CardPullExperience/CardsReveal/types.ts';

import type { AnimatedCards_cardPull } from './__generated__/index.graphql.ts';
import { useAnimatedCardStyles } from './useAnimatedCardStyles.ts';

const StyledCardImg = styled(CardImg)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const AnimatedCard = styled(animated.div)`
  position: absolute;
  user-select: none;
  /* The card artwork layers over the hold-to-confirm target, and the native
     long-press image menu is not suppressed by pointer-events: none. */
  -webkit-touch-callout: none;

  img {
    -webkit-touch-callout: none;
    -webkit-user-drag: none;
  }

  /* fade out cards in fan */
  & button[disabled]::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: var(--c-black);
    opacity: calc(1 - max(0.1, var(--animated-card-overlay-opacity)));
    z-index: 1;
    border-radius: ${getCardBorderRadiusWithOffsets(14)};
  }
`;

const StyledRemainingCardImg = styled(StyledCardImg)`
  opacity: 0.5;
  filter: grayscale(100%);
`;

const CardImageCrossfade = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${CARD_ASPECT_RATIO};
`;

const CrossfadeLayer = styled.div<{ $visible: boolean }>`
  transition: opacity 0.6s ease-in-out;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};

  &:not(:first-child) {
    position: absolute;
    inset: 0;
  }
`;

type Props = {
  cardPull: Nullable<AnimatedCards_cardPull>;
  containerWidth: number;
  containerHeight: number;
  rarity: Rarity;
  uiState: CardRevealUIState;
  setUiState: (uiState: CardRevealUIState) => void;
  onReveal: (pulledCardId: string) => void;
};

type CardPulledCard = AnimatedCards_cardPull['activePulledCards'][number];

export const AnimatedCards = ({
  cardPull,
  containerWidth,
  containerHeight,
  rarity,
  uiState,
  setUiState,
  onReveal,
}: Props) => {
  const [initialUIState] = useState('pickRevealed');

  const cards: CardPulledCard[] = cardPull
    ? cardPull.activePulledCards
    : range(10).map(id => ({
        __typename: 'CardPullCard',
        id: id.toString(),
        revealedCard: null,
        picked: false,
        country: null,
        competition: null,
        division: null,
        position: null,
        fiftyFiftySelected: null,
        quality: null,
        gameplayTier: null,
      }));

  const cardRevealed = cards.some(pulledCard => pulledCard.revealedCard);

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  if (selectedCardId && uiState === 'idle') {
    setSelectedCardId(null);
  }

  const holdLockRef = useRef<string | null>(null);

  const fiftyFiftyRevealed =
    cards.some(card => card.fiftyFiftySelected) &&
    cards.some(card => !card.fiftyFiftySelected);

  const [fiftyFiftyHaveDisappeared, setFiftyFiftyHaveDisapeared] =
    useState(fiftyFiftyRevealed);

  const styles = useAnimatedCardStyles({
    uiState,
    cards,
    containerWidth,
    containerHeight,
    fiftyFiftyHaveDisappeared,
  });

  const visibleCards = useMemo(() => {
    if (fiftyFiftyRevealed) {
      return cards.filter(card => card.fiftyFiftySelected);
    }
    return cards;
  }, [cards, fiftyFiftyRevealed]);

  const transitions = useTransition(visibleCards, {
    from: { opacity: 1 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    onRest: () => {
      if (fiftyFiftyRevealed) {
        setFiftyFiftyHaveDisapeared(true);
      }
    },
    config: config.gentle,
    keys: card => card.id,
  });

  return transitions((styleTransition, pulledCard) => {
    // It is expected that index can only be found in the new cards array.
    // when going from range based cards to real card pull, we'll get -1 values
    // when shuffling cards we'll also get -1
    const index = cards.findIndex(card => card.id === pulledCard.id);

    return (
      <AnimatedCard
        key={pulledCard.id}
        style={{
          ...(index >= 0 ? styles[index] : {}),
          ...styleTransition,
          ...(pulledCard.picked ? { zIndex: 2 } : {}),
        }}
      >
        {pulledCard.picked ? (
          <PickedCardRevealer
            rarity={rarity}
            revealed={!!pulledCard.revealedCard}
            immediate={initialUIState === 'allRevealed'}
            front={
              <StrokedCard
                active
                color={getRarityColor(rarity)}
                strokeWidth={1}
              >
                <CardImageCrossfade>
                  <CrossfadeLayer
                    $visible={
                      !!pulledCard.revealedCard &&
                      !pulledCard.revealedCard.pictureUrl
                    }
                  >
                    {!!pulledCard.revealedCard && (
                      <CardMeta
                        card={pulledCard.revealedCard}
                        avatarUrl={
                          pulledCard.revealedCard.anyPlayer.avatarPictureUrl
                        }
                      />
                    )}
                  </CrossfadeLayer>
                  <CrossfadeLayer
                    $visible={
                      !pulledCard.revealedCard ||
                      !!pulledCard.revealedCard.pictureUrl
                    }
                  >
                    <StyledCardImg
                      src={pulledCard.revealedCard?.pictureUrl}
                      className={classNames({ picked: true })}
                      width={480}
                    />
                  </CrossfadeLayer>
                </CardImageCrossfade>
              </StrokedCard>
            }
            back={
              <RevealingCardPlaceholder
                rarity={rarity}
                selected
                disabled
                cardPulledCard={pulledCard}
              />
            }
            onRest={async () => {
              await waitMs(1000);
              if (['pulled', 'selected'].includes(uiState)) {
                setUiState('pickRevealed');
              }
            }}
          />
        ) : (
          <RemainingCardRevealer
            key={pulledCard.id}
            immediate={initialUIState === 'allRevealed'}
            revealed={
              !!pulledCard.revealedCard &&
              (uiState === 'pickRevealed' ||
                uiState === 'allRevealed' ||
                pulledCard.picked)
            }
            index={index}
            back={
              <RevealingCardPlaceholder
                rarity={rarity}
                selected={
                  selectedCardId === null
                    ? null
                    : selectedCardId === pulledCard.id
                }
                disabled={
                  cardRevealed ||
                  uiState !== 'pulled' ||
                  (selectedCardId !== null && selectedCardId !== pulledCard.id)
                }
                onHoldStart={() => {
                  if (
                    holdLockRef.current !== null &&
                    holdLockRef.current !== pulledCard.id
                  )
                    return false;
                  holdLockRef.current = pulledCard.id;
                  setSelectedCardId(pulledCard.id);
                  return true;
                }}
                onHoldCancel={() => {
                  if (holdLockRef.current === pulledCard.id) {
                    holdLockRef.current = null;
                  }
                  setSelectedCardId(null);
                }}
                onConfirm={() => {
                  if (holdLockRef.current === pulledCard.id) {
                    holdLockRef.current = null;
                  }
                  setSelectedCardId(null);
                  onReveal(pulledCard.id);
                }}
                cardPulledCard={pulledCard}
              />
            }
            front={
              <CardImageCrossfade>
                <CrossfadeLayer
                  $visible={
                    !!pulledCard.revealedCard &&
                    !pulledCard.revealedCard.pictureUrl
                  }
                >
                  {!!pulledCard.revealedCard && (
                    <CardMeta
                      card={pulledCard.revealedCard}
                      avatarUrl={
                        pulledCard.revealedCard.anyPlayer.avatarPictureUrl
                      }
                    />
                  )}
                </CrossfadeLayer>
                <CrossfadeLayer
                  $visible={
                    !pulledCard.revealedCard ||
                    !!pulledCard.revealedCard.pictureUrl
                  }
                >
                  <StyledRemainingCardImg
                    src={pulledCard.revealedCard?.pictureUrl}
                    width={320}
                  />
                </CrossfadeLayer>
              </CardImageCrossfade>
            }
            afterReveal={() => {
              setUiState('allRevealed');
            }}
          />
        )}
        <animated.div
          style={{
            marginTop: 'var(--half-unit)',
            transformOrigin: 'center top',
            // invert scale to avoid footer growing too big
            scale:
              index >= 0
                ? (styles[index] as { scale: SpringValue<number> }).scale.to(
                    x => 1 / Math.max(1, x)
                  )
                : undefined,
          }}
        >
          <AnimatedCardFooter
            pulledCard={pulledCard}
            ready={uiState === 'allRevealed'}
          />
        </animated.div>
      </AnimatedCard>
    );
  });
};

AnimatedCards.fragments = {
  cardPull: gql`
    fragment AnimatedCards_cardPull on CardPull {
      id
      activePulledCards {
        id
        fiftyFiftySelected
        revealedCard {
          slug
          pictureUrl
          anyPlayer {
            slug
            avatarPictureUrl
          }
          ...CardMeta_AnyCardInterface
        }
        picked
        ...RevealingCardPlaceholder_cardPulledCard
        ...useAnimatedCardStyles_cardPullCard
        ...AnimatedCardFooter_cardPulledCard
      }
    }
    ${CardMeta.fragments.AnyCardInterface}
    ${RevealingCardPlaceholder.fragments.cardPulledCard}
    ${useAnimatedCardStyles.fragments.cardPullCard}
    ${AnimatedCardFooter.fragments.cardPulledCard}
  ` as TypedDocumentNode<AnimatedCards_cardPull>,
};
